/**
 * Namespace `modals`: os diálogos que flutuam sobre qualquer módulo (novo
 * projeto/IA, senha, exclusão de conta, configurações do projeto,
 * compartilhamento, publicação, equipe) e o fluxo de importação.
 *
 * Os textos foram RECORTADOS dos componentes sem reescrita. Onde dois modais
 * dizem a mesma palavra ("Cancelar", "Fechar", "Salvar"), o dicionário repete:
 * cada modal é dono do seu texto e pode divergir sem arrastar os outros.
 *
 * REGRA DO QUE FICOU DE FORA: valor persistido não vira rótulo.
 *  - papéis de membro (`owner`/`admin`/`editor`/`viewer`) e tipo de acesso da
 *    publicação (`view`/`edit`) continuam no código; aqui está só o rótulo,
 *    indexado por esse mesmo valor;
 *  - complexidade da IA (`simple`/`medium`/`complex`) e a chave do modelo
 *    (`software`, `website`...) idem;
 *  - a fonte de importação (`trello`, `clickup`, `azure_devops`,
 *    `ms_project`), o nome do produto ("Trello") e o formato do arquivo
 *    ("JSON", "CSV", "XML") são identificadores, não interface;
 *  - os APELIDOS DE CABEÇALHO de planilha que `importParsers` casa com os
 *    campos do sistema ("Nome", "Início", "Task Name", "Fecha de inicio") são
 *    DADO que vem do arquivo do usuário: vivem no parser, em todos os idiomas
 *    ao mesmo tempo, e não neste dicionário.
 */
const modals = {
  /** Novo Projeto: escolha do caminho, galeria de modelos, formulário da IA e preview. */
  ai: {
    title: {
      choose: 'Novo Projeto',
      template: 'Escolher Template',
      input: 'Criar Projeto com IA',
      loading: 'Gerando Plano...',
      preview: 'Plano Gerado',
    },

    /** Os quatro caminhos do passo inicial. */
    paths: {
      blank: {
        title: 'Projeto em branco',
        description: 'Comece do zero com um projeto vazio',
      },
      ai: {
        title: 'Criar com IA',
        description: 'Descreva e a IA monta o cronograma',
      },
      import: {
        title: 'Importar',
        description: 'Trello, ClickUp, Azure DevOps...',
      },
      template: {
        title: 'Usar Template',
        description: 'Modelos prontos por indústria',
      },
    },
    /** `{{plan}}` é o nome do plano mínimo que libera o caminho bloqueado. */
    lockedPlan: 'Disponível no plano {{plan}}',

    back: 'Voltar',
    /** Selo do modelo que o onboarding sugere para o segmento do usuário. */
    recommended: 'Recomendado',

    /** Rodapé de cada card da galeria de modelos. */
    templateStats: {
      phases: { one: '{{count}} fase', other: '{{count}} fases' },
      tasks: { one: '{{count}} tarefa', other: '{{count}} tarefas' },
      members: { one: '{{count}} membro', other: '{{count}} membros' },
    },

    input: {
      descriptionLabel: 'Descreva seu projeto',
      descriptionPlaceholder: 'Ex: Desenvolvimento de um e-commerce de roupas com integração de pagamentos, catálogo de produtos, carrinho de compras e área do cliente...',
      complexityLabel: 'Complexidade',
      startDateLabel: 'Data de início',
      startDatePlaceholder: 'Selecionar data',
      targetLabel: 'Prazo alvo',
    },

    /**
     * Rótulo e detalhe de cada nível. O `value` (`simple`/`medium`/`complex`) é
     * o que vai para a Edge Function e fica no componente.
     */
    complexity: {
      simple: { label: 'Simples', description: '15-20 tarefas, 3-4 fases' },
      medium: { label: 'Médio', description: '20-35 tarefas, 4-5 fases' },
      complex: { label: 'Complexo', description: '35-50 tarefas, 5-6 fases' },
    },

    /**
     * Prazo alvo. O número de semanas é o valor enviado e fica no componente;
     * as três últimas opções ganham a equivalência em meses/ano que o produto
     * já mostrava.
     */
    target: {
      none: 'Sem prazo definido',
      weeks: '{{count}} semanas',
      weeks24: '24 semanas (~6 meses)',
      weeks36: '36 semanas (~9 meses)',
      weeks52: '52 semanas (~1 ano)',
    },

    /** Rodam em ciclo enquanto a IA responde. Ordem preservada. */
    loadingMessages: [
      'Analisando requisitos do projeto...',
      'Definindo fases e marcos...',
      'Calculando durações realistas...',
      'Montando a equipe ideal...',
      'Organizando o cronograma...',
      'Finalizando o plano...',
    ],
    loadingHint: 'Isso pode levar alguns segundos',

    preview: {
      badgeTemplate: 'Modelo',
      badgeAI: 'IA',
      members: { one: '{{count}} membro', other: '{{count}} membros' },
      tasks: { one: '{{count}} tarefa', other: '{{count}} tarefas' },
      phases: { one: '{{count}} fase', other: '{{count}} fases' },
      workDays: { one: '~{{count}} dia útil', other: '~{{count}} dias úteis' },
      /** Contador de itens dentro de uma fase, na árvore do preview. */
      items: { one: '{{count}} item', other: '{{count}} itens' },
      /** Só aparece quando a IA falhou e o plano caiu num modelo da biblioteca. */
      fallbackTitle: 'Este plano foi gerado a partir de um template da biblioteca.',
      fallbackReason: 'A IA não pôde ser acionada.',
      fallbackRetry: 'Clique em "Regenerar" para tentar novamente com IA.',
      teamTitle: 'Equipe sugerida',
    },

    actions: {
      generate: 'Gerar Plano',
      changeTemplate: 'Trocar modelo',
      regenerate: 'Regenerar',
      create: 'Criar Projeto',
      creating: 'Criando...',
    },

    errors: {
      minDescription: 'Descreva seu projeto com pelo menos 10 caracteres.',
    },

    toasts: {
      templateFallback: 'Usando template da biblioteca.',
      aiSuccess: 'Plano gerado com inteligência artificial!',
      /** `{{tasks}}` e `{{members}}` já vêm formatados com o plural certo. */
      created: 'Projeto criado com {{tasks}} e {{members}}!',
      createdTasks: { one: '{{count}} tarefa', other: '{{count}} tarefas' },
      createdMembers: { one: '{{count}} membro', other: '{{count}} membros' },
    },
  },

  /** Troca de senha do próprio usuário. */
  changePassword: {
    title: 'Alterar Senha',
    successTitle: 'Senha alterada com sucesso!',
    successMessage: 'Sua senha foi atualizada.',
    newLabel: 'Nova senha',
    newPlaceholder: 'Digite sua nova senha',
    hint: 'Mínimo de 6 caracteres',
    confirmLabel: 'Confirmar nova senha',
    confirmPlaceholder: 'Confirme sua nova senha',
    cancel: 'Cancelar',
    submit: 'Alterar Senha',
    submitting: 'Alterando...',
    errors: {
      tooShort: 'A senha deve ter pelo menos 6 caracteres',
      mismatch: 'As senhas não coincidem',
      samePassword: 'A nova senha deve ser diferente da senha atual.',
      generic: 'Erro ao alterar senha',
    },
  },

  /** Exclusão de conta com janela de arrependimento de 30 dias. */
  deleteAccount: {
    title: 'Excluir minha conta',
    subtitle: 'Esta ação não pode ser desfeita após 30 dias',
    effectsTitle: 'Ao excluir sua conta:',
    effects: {
      subscription: 'Sua assinatura será cancelada imediatamente',
      sharedProjects: 'Você será removido de projetos compartilhados',
      soleMemberProjects: 'Projetos que só você é membro serão excluídos',
      transferOwnership: 'Projetos com outros membros terão a propriedade transferida',
      /** A frase quebra em três porque "30 dias" vai em negrito no meio dela. */
      cancelWindowBefore: 'Você tem',
      cancelWindowStrong: '30 dias',
      cancelWindowAfter: 'para cancelar a exclusão fazendo login',
      afterWindow: 'Após 30 dias, seus dados serão permanentemente removidos',
    },
    reasonLabel: 'Motivo (opcional)',
    reasonPlaceholder: 'Por que está saindo?',
    /** Mesma quebra: o e-mail do usuário vai em negrito no meio da frase. */
    confirmBefore: 'Digite',
    confirmAfter: 'para confirmar',
    emailPlaceholder: 'Seu email',
    cancel: 'Cancelar',
    confirm: 'Excluir minha conta',
    deleting: 'Excluindo...',
    /** `{{date}}` já vem formatado no idioma do usuário. */
    scheduled: 'Conta marcada para exclusão. Seus dados serão removidos em {{date}}.',
    error: 'Erro ao solicitar exclusão',
  },

  /** Calendário do projeto: dias úteis, jornada e feriados. */
  projectSettings: {
    title: 'Configurações de projeto',
    workDaysTitle: 'Dias úteis',
    workDaysHint: 'Selecione os dias da semana que são considerados dias úteis',
    /**
     * Domingo primeiro, para casar com `Date.getDay()` — o índice É o valor
     * gravado em `settings.workDays`. Não reordene.
     */
    weekDays: [
      'Domingo',
      'Segunda-feira',
      'Terça-feira',
      'Quarta-feira',
      'Quinta-feira',
      'Sexta-feira',
      'Sábado',
    ],
    hoursTitle: 'Duração do dia (horas)',
    holidaysTitle: 'Dias não úteis (feriados, exceções)',
    /**
     * DD/MM continua em todos os idiomas porque é o que `parseHolidayDate` lê
     * (dia primeiro, sempre). Mudar a ordem no texto ensinaria o usuário a
     * digitar o que o parser recusa.
     */
    holidaysHint: 'Digite ou cole datas aqui (DD/MM/AA), separadas por vírgulas',
    holidaysPlaceholder: '25/12/24, 01/01/25, 21/04/25...',
    daysConfigured: { one: '{{count}} dia configurado', other: '{{count}} dias configurados' },
    edit: 'Editar...',
    cancel: 'Cancelar',
    apply: 'Aplicar',
    confirm: 'OK',
  },

  /** Convite de pessoas para o projeto, por e-mail, com papel. */
  projectShare: {
    title: 'Compartilhar Projeto',
    /**
     * Chave = papel gravado em `project_members.role`. Só o rótulo e a
     * descrição vêm daqui; o valor continua no componente.
     */
    roles: {
      owner: { label: 'Proprietário', description: 'Controle total' },
      admin: { label: 'Admin', description: 'Gerencia membros e configurações' },
      editor: { label: 'Editor', description: 'Pode editar tarefas' },
      viewer: { label: 'Visualizador', description: 'Somente leitura' },
    },
    emailPlaceholder: 'Email do usuário',
    add: 'Adicionar',
    limitReached: {
      one: 'Limite de {{count}} membro atingido',
      other: 'Limite de {{count}} membros atingido',
    },
    upgrade: 'Fazer upgrade',
    /** Contador do plano: quantos membros de quantos permitidos. */
    memberCount: '{{count}} / {{limit}} membros',
    empty: 'Nenhum membro encontrado',
    /** Marca a linha do próprio usuário na lista. */
    you: '(você)',
    /** Quando o convidado ainda não tem nome nem e-mail carregados. */
    unknownUser: 'Usuário',
    toasts: {
      invalidEmail: 'Insira um email válido',
      addError: 'Erro ao adicionar membro',
      inviteEmailFailed: 'Convite criado para {{email}}, mas falha no envio do email. Compartilhe o link manualmente.',
      inviteSent: 'Convite enviado para {{email}}',
      memberAdded: 'Membro adicionado com sucesso',
      roleError: 'Erro ao alterar permissão',
      removeError: 'Erro ao remover membro',
      memberRemoved: 'Membro removido',
    },
  },

  /** Publicação do projeto num link público, com senha opcional. */
  publish: {
    title: 'Publicar e Compartilhar',
    lockedTitle: 'Compartilhamento não disponível',
    /** `{{plan}}` é o nome do plano mínimo que libera a publicação. */
    lockedDescription: 'Faça upgrade para o plano {{plan}} para compartilhar seus projetos.',
    upgrade: 'Fazer Upgrade',
    toggleTitle: 'Publicar projeto',
    toggleDescription: 'Qualquer pessoa com o link poderá visualizar o projeto',
    linkLabel: 'Link do projeto',
    /** Ocupa o campo do link antes de o token existir. */
    linkPending: 'Link será gerado ao salvar...',
    linkHint: 'O link será gerado ao salvar',
    copy: 'Copiar',
    copied: 'Copiado!',
    lensesLabel: 'O que o link mostra',
    lensesHint: 'Escolha por link. O cliente de obra costuma querer só o cronograma; o de agência também quer o quadro.',
    /** Chave = valor gravado em `settings.publish.views`. */
    lenses: {
      gantt: {
        title: 'Gantt',
        description: 'Datas, marcos e progresso de cada entrega',
      },
      kanban: {
        title: 'Kanban',
        description: 'Em que etapa cada trabalho está agora',
      },
      eap: {
        title: 'EAP',
        description: 'A decomposição do escopo, do pacote de trabalho para cima',
      },
    },
    lensesMinHint: 'Marque quantas quiser. Ao menos uma precisa ficar marcada, senão o link não mostra nada.',
    showAssigneesToggle: 'Mostrar responsáveis',
    showAssigneesHint: 'Desligado, o visitante vê as entregas sem os nomes de quem executa.',
    passwordToggle: 'Proteger com senha',
    /** `{{plan}}` é o plano mínimo que libera a senha no link. */
    passwordLocked: 'Disponível no plano {{plan}}',
    passwordPlaceholder: 'Digite a senha de acesso',
    showPassword: 'Mostrar senha',
    hidePassword: 'Ocultar senha',
    privateTitle: 'Projeto privado',
    privateDescription: 'Ative a publicação para gerar um link compartilhável',
    cancel: 'Cancelar',
    close: 'Fechar',
    save: 'Salvar',
    saving: 'Salvando...',
    published: 'Publicado!',
  },

  /** Equipe do projeto: membros locais e importação do pool de recursos. */
  team: {
    title: 'Equipe do Projeto',
    limitBanner: {
      one: 'Limite de {{count}} membro atingido. Faça upgrade para adicionar mais.',
      other: 'Limite de {{count}} membros atingido. Faça upgrade para adicionar mais.',
    },
    /** Contador discreto de uso do plano. */
    usage: '{{count}}/{{limit}} membros',
    emptyTitle: 'Nenhum membro na equipe',
    emptyHint: 'Adicione membros para atribuir tarefas',
    namePlaceholder: 'Nome',
    newNamePlaceholder: 'Nome do membro',
    rolePlaceholder: 'Cargo (ex: Engenheiro Civil)',
    emailPlaceholder: 'Email (opcional)',
    save: 'Salvar',
    saving: 'Salvando...',
    cancel: 'Cancelar',
    edit: 'Editar',
    remove: 'Remover',
    add: 'Adicionar',
    adding: 'Adicionando...',
    addMember: 'Adicionar membro',
    limitTooltip: {
      one: 'Limite de {{count}} membro atingido',
      other: 'Limite de {{count}} membros atingido',
    },
    pool: {
      title: 'Importar do Pool de Recursos',
      loading: 'Carregando recursos...',
      empty: 'Todos os recursos do pool já estão na equipe',
      add: 'Adicionar à equipe',
      button: 'Importar do Pool',
      tooltip: 'Importar do pool de recursos (Capacidade)',
    },
    close: 'Fechar',
    confirmDelete: {
      title: 'Remover membro',
      message: 'Remover este membro da equipe?',
      confirm: 'Remover',
    },
  },

  /** Importação de projeto vindo de Trello, ClickUp, Azure DevOps e MS Project. */
  import: {
    title: {
      select: 'Importar de Outro Sistema',
      /** `{{source}}` é o nome do produto de origem, que não se traduz. */
      upload: 'Importar do {{source}}',
      preview: 'Confirmar Importação',
      done: 'Importação Concluída',
    },
    description: {
      select: 'Selecione o sistema de origem para importar seu projeto',
      upload: 'Faça upload do arquivo exportado',
      preview: 'Verifique os dados antes de importar',
    },
    /** Chave = `ImportSource`. O nome do produto e o formato ficam no componente. */
    sources: {
      trello: 'Boards, listas e cards',
      clickup: 'Tarefas, subtarefas e status',
      azure_devops: 'Work items, epics e features',
      ms_project: 'WBS, tarefas e dependências',
    },
    /** Onde clicar, em cada produto, para gerar o arquivo de exportação. */
    instructions: {
      trello: 'No Trello, abra o board → Menu (⋯) → Mais → Imprimir e Exportar → Exportar como JSON',
      clickup: 'No ClickUp, abra a lista → ⋯ → Exportar → CSV',
      azure_devops: 'No Azure DevOps, vá em Boards → Work Items → Selecione os itens → Export to CSV',
      ms_project: 'No MS Project, vá em Arquivo → Salvar Como → Tipo: XML (*.xml)',
    },
    back: 'Voltar',
    dropzone: 'Clique para selecionar o arquivo',
    /** `{{format}}` é a extensão aceita (JSON, CSV, XML). */
    formatHint: 'Formato: {{format}} (máx. 10MB)',
    stats: {
      tasks: 'Tarefas',
      groups: 'Grupos',
      roots: 'Raízes',
    },
    projectNameLabel: 'Nome do projeto',
    previewLabel: 'Preview das tarefas',
    moreTasks: {
      one: '... e mais {{count}} tarefa',
      other: '... e mais {{count}} tarefas',
    },
    importButton: 'Importar Projeto',
    importing: 'Importando...',
    /** `{{done}}` de `{{total}}` lotes já gravados. */
    importingProgress: 'Importando {{done}} de {{total}}...',
    success: 'Projeto "{{name}}" importado com sucesso!',
    close: 'Fechar',
    errors: {
      fileTooLarge: 'Arquivo muito grande. Limite: 10MB',
      tooManyTasks: 'Limite de 5000 tarefas por importação. O arquivo contém {{count}} tarefas.',
      parse: 'Erro ao processar arquivo',
      generic: 'Erro ao importar',
      planLimit: 'Limite de projetos do seu plano atingido. Faça upgrade para importar.',
      /** `{{message}}` é a mensagem crua que subiu do serviço. */
      withMessage: 'Erro ao importar: {{message}}',
    },

    /**
     * Textos que o PARSER produz (avisos, erros e nomes provisórios) e que o
     * diálogo só exibe. Ficam aqui, e não no parser, porque são interface; o
     * parser os recebe por parâmetro para continuar sendo função pura.
     */
    parser: {
      /** Nome do projeto quando o arquivo de origem não traz um. */
      trelloProject: 'Projeto Trello',
      clickupProject: {
        one: 'Projeto ClickUp ({{count}} tarefa)',
        other: 'Projeto ClickUp ({{count}} tarefas)',
      },
      azureProject: {
        one: 'Projeto Azure DevOps ({{count}} item)',
        other: 'Projeto Azure DevOps ({{count}} itens)',
      },
      msProject: 'Projeto MS Project',
      /** Membro do Trello sem nome nem usuário no export. */
      unknownMember: 'Membro',
      /** `{{id}}` é o UID da tarefa no arquivo, ou a posição na lista. */
      unnamedTask: 'Tarefa {{id}}',
      warnings: {
        trelloNoDependencies: 'Trello não possui dependências entre tarefas, datas de início ou durações.',
        trelloManyCards: {
          one: '{{count}} card importado. Verifique se todos são necessários.',
          other: '{{count}} cards importados. Verifique se todos são necessários.',
        },
        clickupNoDependencies: 'Dependências entre tarefas não são exportadas no CSV do ClickUp.',
        azureNoDependencies: 'Dependências entre work items não são exportadas no CSV do Azure DevOps.',
        azureIterations: 'Iteration Paths foram ignorados (sprints precisam ser criados manualmente).',
        unnamedTasks: {
          one: '{{count}} tarefa sem nome recebeu um nome provisório.',
          other: '{{count}} tarefas sem nome receberam um nome provisório.',
        },
        dependencies: {
          one: '{{count}} dependência de precedência será importada.',
          other: '{{count}} dependências de precedência serão importadas.',
        },
      },
      errors: {
        trelloInvalidJson: 'Arquivo JSON do Trello inválido',
        trelloEmpty: 'Board do Trello vazio: nenhuma lista ou card encontrado',
        /** `{{message}}` é o erro cru do Papa Parse. */
        clickupCsv: 'Erro ao processar CSV do ClickUp: {{message}}',
        clickupEmpty: 'CSV do ClickUp vazio: nenhuma tarefa encontrada',
        azureCsv: 'Erro ao processar CSV do Azure DevOps: {{message}}',
        azureEmpty: 'CSV do Azure DevOps vazio: nenhum work item encontrado',
        mspInvalidXml: 'Arquivo XML do MS Project inválido',
        mspNoProjectElement: 'Elemento <Project> não encontrado no XML',
        mspNoTasks: 'Nenhuma tarefa encontrada no arquivo XML do MS Project',
        /** `{{source}}` é o identificador da fonte, que não se traduz. */
        unsupportedSource: 'Fonte de importação não suportada: {{source}}',
      },
    },
  },
};

/** en e es derivam desta forma: chave faltando vira erro de compilação. */
export type modalsCopy = typeof modals;

export default modals;
